import React, { createContext, useContext, useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  loginConfirmedAction,
  setUserProfileAction,
  Logout as ReduxLogout,
} from '../store/actions/AuthActions';
import { useNavigate } from 'react-router-dom';
import { getProfile } from '../services/AuthService';

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const tempUser = localStorage.getItem('user');
  const tempUserDetails = localStorage.getItem('userDetails');

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const reduxToken = useSelector((state) => state.auth.token);
  const loading = useSelector((state) => state.auth.loading);

  const [user, setUser] = useState(tempUser ? JSON.parse(tempUser) : null);
  const [loadingUser, setLoadingUser] = useState(true);
  const [authToken, setAuthToken] = useState(null);

  useEffect(() => {
    const userDetails = tempUserDetails ? JSON.parse(tempUserDetails) : null;

    const handleProfileFetch = async () => {
      try {
        const profile = await getProfile();
        if (profile?.data?.allowedModules) {
          dispatch(setUserProfileAction(profile.data));
          setUser(profile.data);
          localStorage.setItem('user', JSON.stringify(profile.data));
          if (profile.data.mustChangePassword && window.location.pathname !== '/change-password') {
            navigate('/change-password');
          }
        }
      } catch (error) {
        console.log('Error fetching profile:', error);
        if (error.response && error.response.status === 401) {
          logout();
        }
      }
    };

    if (userDetails?.token) {
      setAuthToken(userDetails.token);
      dispatch(loginConfirmedAction(userDetails));
      handleProfileFetch();
    } else if (reduxToken) {
      setAuthToken(reduxToken);
      handleProfileFetch();
    } else {
      const publicPaths = [
        '/login',
        '/logout',
        '/external-login',
        '/login/callback',
        '/forgot-password',
        '/reset-password',
      ];
      if (!publicPaths.includes(window.location.pathname)) {
        logout();
      }
    }
    // No else block - if no token, just stay logged out. ProtectedRoute will handle redirection.

    setLoadingUser(false);
  }, [dispatch, navigate, tempUserDetails, reduxToken]);

  const refreshProfile = async () => {
    const profile = await getProfile();
    if (profile?.data) {
      dispatch(setUserProfileAction(profile.data));
      setUser(profile.data);
      localStorage.setItem('user', JSON.stringify(profile.data));
    }
    return profile;
  };

  const login = (userData) => {
    setUser(userData);
    setAuthToken(userData.token);
    localStorage.setItem('user', JSON.stringify(userData));
  };

  const logout = () => {
    setUser(null);
    setAuthToken(null);
    localStorage.removeItem('user');
    localStorage.removeItem('userDetails');
    localStorage.clear();
    const publicPaths = ['/login', '/external-login'];
    if (!publicPaths.includes(window.location.pathname)) {
      navigate('/login');
    }
  };

  const hasPermission = (module, operations) => {
    if (!user || Object.keys(user).length === 0) return false;
    const modulePermissions = user.allowedModules.find((mod) => mod.value === module);
    return modulePermissions && operations?.some((op) => modulePermissions.operations.includes(op));
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        login,
        logout,
        refreshProfile,
        hasPermission,
        isAuthenticated: () => !!authToken,
        loading: loading || loadingUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
